// SLEEPSAKE: the buy box's live offer figures — the sale pill by the title,
// the "Save £59" pill in the price row and the "That's 16p a night" line.
// Each is server-rendered for the default pick and repainted from the same
// `bundle:change` detail that repaints the price above them
// (`bundle-selector.js`), so the three can never disagree with that price.

import { ThemeEvents, BundleChangeEvent } from "@theme/events";
import { formatMoneyWithoutTrailingZeros } from "@theme/money-formatting";
import { perNightCents, smallCoinSuffix } from "@theme/bundle-pricing";

/**
 * @typedef {object} OfferFigures
 * @property {number} qty pillows in the pick
 * @property {number} bundlePrice total in minor units
 * @property {number} bundleCompare compare-at total in minor units
 * @property {number} totalDiscountPct rounded percentage off compare-at
 * @property {number} bundleSavingsFloor saving floored to whole units, as the bundle cards show it
 */

class OfferFigure extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const section = this.closest(".shopify-section");
    if (!section) return;

    section.addEventListener(
      BundleChangeEvent.eventName,
      (event) => {
        const detail = /** @type {any} */ (event).detail;
        if (detail && detail.sectionId === this.dataset.sectionId) this.paint(detail);
      },
      { signal },
    );
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // The selector may have upgraded first and already announced its pick.
    this.#paintFromSelector(section);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const resource = /** @type {any} */ (event).detail?.resource;
    if (!resource) return;
    const section = this.closest(".shopify-section");
    // The selector reads the new variant in its own listener; read after it.
    queueMicrotask(() => {
      if (section && this.#paintFromSelector(section)) return;
      const price = Number(resource.price) || 0;
      const compare = Math.max(Number(resource.compare_at_price) || 0, price);
      this.paint({
        qty: 1,
        bundlePrice: price,
        bundleCompare: compare,
        totalDiscountPct: compare > price ? Math.round(((compare - price) / compare) * 100) : 0,
        bundleSavingsFloor: compare > price ? Math.floor((compare - price) / 100) * 100 : 0,
      });
    });
  };

  /**
   * @param {Element} section
   * @returns {boolean} whether a bundle pick was found and painted
   */
  #paintFromSelector(section) {
    const selector = /** @type {any} */ (section.querySelector("bundle-selector"));
    const detail = typeof selector?.getCurrentDetail === "function" ? selector.getCurrentDetail() : null;
    if (!detail) return false;
    this.paint(detail);
    return true;
  }

  /** @param {number} minorUnits */
  money(minorUnits) {
    const format =
      (this.dataset.currencyCodeEnabled === "true" ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat) ||
      "${{amount}}";
    return formatMoneyWithoutTrailingZeros(minorUnits, format, this.dataset.currencyCode || "");
  }

  /** @param {OfferFigures} _detail */
  paint(_detail) {}
}

/** "50% off" by the title; hidden when nothing is off. */
class OfferDiscountPill extends OfferFigure {
  /** @param {OfferFigures} detail */
  paint(detail) {
    const pct = Number(detail.totalDiscountPct) || 0;
    this.hidden = pct <= 0;
    const figure = this.querySelector("[data-offer-pct]");
    if (figure) figure.textContent = String(pct);
  }
}

/** "Save £59" in the price row, floored like the bundle cards' own Save. */
class OfferSavings extends OfferFigure {
  /** @param {OfferFigures} detail */
  paint(detail) {
    const saving = Number(detail.bundleSavingsFloor) || 0;
    this.hidden = saving <= 0;
    const figure = this.querySelector("[data-offer-saving]");
    if (figure) figure.textContent = this.money(saving);
  }
}

/** "That's 16p a night over its 1-year warranty". */
class PerNightCost extends OfferFigure {
  /** @param {OfferFigures} detail */
  paint(detail) {
    const cents = perNightCents(Number(detail.bundlePrice) || 0, parseInt(this.dataset.days || "365", 10));
    const suffix = smallCoinSuffix(this.dataset.currencyCode || "");
    const figure = this.querySelector("[data-per-night-figure]");
    if (figure) figure.textContent = cents < 100 && suffix ? `${cents}${suffix}` : this.money(cents);

    const tail = this.querySelector("[data-per-night-tail]");
    const tailText = (Number(detail.qty) || 1) > 1 ? this.dataset.tailMulti : this.dataset.tailSingle;
    if (tail && tailText) tail.textContent = tailText;
  }
}

for (const [name, element] of /** @type {const} */ ([
  ["offer-discount-pill", OfferDiscountPill],
  ["offer-savings", OfferSavings],
  ["per-night-cost", PerNightCost],
])) {
  if (!customElements.get(name)) customElements.define(name, element);
}
